import React from 'react'
import { css } from '@mxenabled/cssinjs'
import { addDays } from 'date-fns/addDays'
import { addMonths } from 'date-fns/addMonths'
import { eachDayOfInterval } from 'date-fns/eachDayOfInterval'
import { eachWeekOfInterval } from 'date-fns/eachWeekOfInterval'
import { endOfMonth } from 'date-fns/endOfMonth'
import { format } from 'date-fns/format'
import { isBefore } from 'date-fns/isBefore'
import { isSameMonth } from 'date-fns/isSameMonth'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfToday } from 'date-fns/startOfToday'
import { subMonths } from 'date-fns/subMonths'

import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'

import { Text } from '@mxenabled/mxui'

import Header from './Header'
import Day from './Day'

export const CALENDAR_WIDTH = 340
export const DATE_SIZE = 44
export const enum CalendarSelectionTypes {
  None = 'None',
  Single = 'Single',
  Range = 'Range',
}

export type CalendarCopy = {
  nextAria: string
  prevAria: string
  today: string
}

export interface CalendarProps {
  copy: CalendarCopy
  beginDate?: Date
  endDate?: Date
  onDateSelected?: (date: Date) => void
  onRangeSelected?: (beginDate: Date, endDate: Date) => void
  selectionType?: CalendarSelectionTypes
}

const Calendar: React.FC<CalendarProps> = ({
  copy,
  beginDate: beginDateProp = undefined,
  endDate: endDateProp = undefined,
  onDateSelected,
  onRangeSelected,
  selectionType = CalendarSelectionTypes.Single,
}) => {
  const styles = getStyles()

  const [date, setDate] = React.useState(startOfToday)

  const [beginDate, setBeginDate] = React.useState<Date | undefined>(beginDateProp)
  const [endDate, setEndDate] = React.useState<Date | undefined>(endDateProp)

  React.useEffect(() => {
    setBeginDate(beginDateProp)
    setEndDate(endDateProp)
    if (beginDateProp) {
      setDate(beginDateProp)
    }
  }, [beginDateProp, endDateProp])

  const weeks = React.useMemo(() => {
    const weeksInInterval = eachWeekOfInterval({ start: startOfMonth(date), end: endOfMonth(date) })

    const weeks = [] as Date[][]
    for (let i = 0; i < weeksInInterval.length; i++) {
      const weekStart = weeksInInterval[i]
      const days = eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) })

      const week = [] as Date[]
      for (let j = 0; j < days.length; j++) {
        week.push(days[j])
      }
      weeks.push(week)
    }

    return weeks
  }, [date])

  const handleSelection = (date: Date) => {
    if (selectionType === CalendarSelectionTypes.None) return

    if (selectionType === CalendarSelectionTypes.Single) {
      setBeginDate(date)
      onDateSelected?.(date)
      return
    }

    if (!beginDate || endDate) {
      setBeginDate(date)
      setEndDate(undefined)
    } else if (isBefore(date, beginDate)) {
      setEndDate(beginDate)
      setBeginDate(date)
      onRangeSelected?.(date, beginDate)
    } else {
      setEndDate(date)
      onRangeSelected?.(beginDate, date)
    }
  }

  const handleTodayClick = () => {
    const today = startOfToday()
    setDate(today)

    if (selectionType === CalendarSelectionTypes.None) return

    setBeginDate(today)

    if (selectionType === CalendarSelectionTypes.Single) {
      onDateSelected?.(today)
    } else {
      setEndDate(today)
      onRangeSelected?.(today, today)
    }
  }

  const handleNextMonthClick = () => {
    setDate(addMonths(date, 1))
  }

  const handlePreviousMonthClick = () => {
    setDate(subMonths(date, 1))
  }

  return (
    <div className={`mx-cmn-calendar ${styles}`}>
      <Paper sx={{ boxShadow: 'none' }}>
        <Header
          copy={copy}
          date={date}
          onNextClick={handleNextMonthClick}
          onPreviousClick={handlePreviousMonthClick}
        />

        <div className="mx-cmn-calendar-header-row">
          {weeks[0].map((day, index) => (
            <div className="mx-cmn-calendar-header-cell" key={index}>
              <Text bold={true} variant="body2">
                {format(day, 'EEEEE')}
              </Text>
            </div>
          ))}
        </div>

        {weeks.map((week, index) => (
          <div className="mx-cmn-calendar-week" key={`w-${index}`}>
            {week.map((day, index) => (
              <Day
                beginDate={beginDate}
                date={day}
                endDate={endDate}
                isInMonth={isSameMonth(day, date)}
                key={index}
                onClick={handleSelection}
              />
            ))}
          </div>
        ))}

        <div className="mx-cmn-calendar-today-container">
          <Button onClick={handleTodayClick} size="small" variant="text">
            {copy.today}
          </Button>
        </div>
      </Paper>
    </div>
  )
}

const getStyles = () =>
  css({
    width: CALENDAR_WIDTH,
    '& .MuiPaper-root': {
      paddingLeft: 24,
      paddingRight: 24,
      paddingTop: 4,
      paddingBottom: 4,
    },
    '& .mx-cmn-calendar-header-row': {
      display: 'flex',
      justifyContent: 'center',
      textAlign: 'center',
      '& .mx-cmn-calendar-header-cell': {
        height: DATE_SIZE,
        width: DATE_SIZE,
        minWidth: DATE_SIZE,
        '& .kmui-text': {
          alignItems: 'center',
          display: 'flex',
          height: '100%',
          justifyContent: 'center',
          width: '100%',
        },
      },
    },
    '& .mx-cmn-calendar-week': {
      display: 'flex',
      justifyContent: 'center',
      textAlign: 'center',
    },
    '& .mx-cmn-calendar-today-container': {
      display: 'block',
      marginTop: 16,
      textAlign: 'center',
      width: '100%',
      '& > .MuiButton-root': {
        display: 'inline-block',
        marginLeft: 'auto',
        marginRight: 'auto',
      },
    },
  })

export default Calendar
